/**
 * ApiService - Service de base pour les appels API
 * @class ApiService
 */
export class ApiService {
    /**
     * Crée une instance d'ApiService
     * @param {string} baseUrl - URL de base de l'API
     */
    constructor(baseUrl = '') {
        this._baseUrl = baseUrl;
        this._defaultHeaders = {
            'Content-Type': 'application/json'
        };
    }

    // Getters
    get baseUrl() { return this._baseUrl; }

    // Setters
    set baseUrl(value) { this._baseUrl = value; }

    /**
     * Effectue une requête GET
     * @param {string} endpoint - Point d'entrée de l'API
     * @param {Object} params - Paramètres de requête
     * @returns {Promise<any>} Données de la réponse
     */
    async get(endpoint, params = {}) {
        const url = this._buildUrl(endpoint, params);
        return this._request(url, { method: 'GET' });
    }

    /**
     * Effectue une requête POST
     * @param {string} endpoint - Point d'entrée de l'API
     * @param {Object} data - Données à envoyer
     * @returns {Promise<any>} Données de la réponse
     */
    async post(endpoint, data = {}) {
        const url = this._buildUrl(endpoint);
        return this._request(url, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    /**
     * Construit l'URL complète avec les paramètres
     * @param {string} endpoint - Point d'entrée
     * @param {Object} params - Paramètres de requête
     * @returns {string} URL complète
     * @private
     */
    _buildUrl(endpoint, params = {}) {
        // Assure que le baseUrl se termine par / et l'endpoint ne commence pas par /
        let base = this._baseUrl.endsWith('/') ? this._baseUrl : this._baseUrl + '/';
        let path = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        
        const url = new URL(path, base);
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                url.searchParams.append(key, value);
            }
        });

        return url.toString();
    }

    /**
     * Effectue une requête HTTP
     * @param {string} url - URL de la requête
     * @param {Object} options - Options de la requête
     * @returns {Promise<any>} Données de la réponse
     * @private
     */
    async _request(url, options = {}) {
        const config = {
            ...options,
            headers: {
                ...this._defaultHeaders,
                ...options.headers
            }
        };

        try {
            const response = await fetch(url, config);

            if (!response.ok) {
                throw new ApiError(
                    `HTTP Error: ${response.status} ${response.statusText}`,
                    response.status,
                    await response.text()
                );
            }

            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }

            return await response.text();
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(`Network Error: ${error.message}`, 0, null);
        }
    }
}

/**
 * Classe d'erreur personnalisée pour les API
 */
export class ApiError extends Error {
    constructor(message, status, data) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}
